import React, { useRef, useMemo } from "react";
import { scaleLinear } from "d3-scale";
import { max } from "d3-array";
import { useChartDimensions } from "./common/utils";

const MARGIN = { top: 30, right: 30, bottom: 55, left: 70 };

// Renders a histogram from { x: bin_edges (n+1), y: counts (n) }
const EventSpaceGraph = ({ graphData }) => {
    const ref = useRef(null);
    const { width, height } = useChartDimensions(ref);

    const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
    const innerHeight = Math.max(0, (height || 450) - MARGIN.top - MARGIN.bottom);

    const { xScale, yScale, bars } = useMemo(() => {
        if (!graphData?.x?.length || !graphData?.y?.length) return {};
        const xScale = scaleLinear()
            .domain([graphData.x[0], graphData.x[graphData.x.length - 1]])
            .range([0, innerWidth]);
        const yScale = scaleLinear()
            .domain([0, max(graphData.y)])
            .nice()
            .range([innerHeight, 0]);
        const bars = graphData.y.map((count, i) => ({
            x: xScale(graphData.x[i]),
            width: Math.max(0, xScale(graphData.x[i + 1]) - xScale(graphData.x[i]) - 1),
            y: yScale(count),
            height: innerHeight - yScale(count),
        }));
        return { xScale, yScale, bars };
    }, [graphData, innerWidth, innerHeight]);

    if (!bars || !xScale) {
        return <div ref={ref} style={{ height: 450 }}><p>No data</p></div>;
    }

    const xTicks = xScale.ticks(6);
    const yTicks = yScale.ticks(5);

    return (
        <div ref={ref} style={{ height: 450 }} className="graphborder graphborder-dashed border-cyan-500">
            <svg width={width} height={height || 450}>
                <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
                    {/* Grid lines */}
                    {yTicks.map(t => (
                        <line key={t} x1={0} x2={innerWidth} y1={yScale(t)} y2={yScale(t)}
                            stroke="#e5e7eb" strokeWidth={1} />
                    ))}

                    {/* Bars */}
                    {bars.map((b, i) => (
                        <rect key={i} x={b.x} y={b.y} width={b.width} height={b.height}
                            fill="rgb(84,162,75)" fillOpacity={0.75} stroke="white" strokeWidth={0.5} />
                    ))}

                    {/* X axis */}
                    <g transform={`translate(0,${innerHeight})`}>
                        <line x1={0} x2={innerWidth} stroke="#374151" />
                        {xTicks.map(t => (
                            <g key={t} transform={`translate(${xScale(t)},0)`}>
                                <line y2={5} stroke="#374151" />
                                <text y={18} textAnchor="middle" fontSize={10}>
                                    {t.toFixed(1)}
                                </text>
                            </g>
                        ))}
                        <text x={innerWidth / 2} y={42} textAnchor="middle" fontSize={11} fill="#374151">
                            Normalised IEI
                        </text>
                    </g>

                    {/* Y axis */}
                    <g>
                        <line y1={0} y2={innerHeight} stroke="#374151" />
                        {yTicks.map(t => (
                            <g key={t} transform={`translate(0,${yScale(t)})`}>
                                <line x1={-5} stroke="#374151" />
                                <text x={-8} dy="0.32em" textAnchor="end" fontSize={10}>
                                    {t.toFixed(0)}
                                </text>
                            </g>
                        ))}
                        <text transform={`translate(-52,${innerHeight / 2}) rotate(-90)`}
                            textAnchor="middle" fontSize={11} fill="#374151">
                            Count
                        </text>
                    </g>
                </g>
            </svg>
        </div>
    );
};

export default EventSpaceGraph;
